import {
	addDays,
	addMonths,
	addWeeks,
	eachDayOfInterval,
	endOfDay,
	endOfWeek,
	format,
	startOfDay,
	startOfMonth,
	startOfWeek,
} from 'date-fns';
import { CalendarViewMode } from '../types';

/**
 * react-day-picker (en-US) and our week/day grids all treat Sunday as the first
 * day of the week. Keep this constant so every view agrees on week boundaries.
 */
export const WEEK_STARTS_ON = 0 as const; // Sunday

export interface IDateRange {
	/** Inclusive start of the visible window (start of day). */
	start: Date;
	/** Inclusive end of the visible window (end of day). */
	end: Date;
}

/**
 * Compute the visible window for a given view mode and focused date. This window
 * is both what the grid renders and what the data layer fetches.
 *
 * - month: the full 6-week grid (leading/trailing days from adjacent months included)
 * - week: Sunday → Saturday containing the focused date
 * - day: the focused date only
 */
export function getVisibleRange(viewMode: CalendarViewMode, focusedDate: Date): IDateRange {
	switch (viewMode) {
		case 'month': {
			// Always a fixed 6-week (42-day) grid so the month view's height stays constant
			// month-to-month; MonthView relies on this to slice `days` into exactly 6 rows of 7.
			const gridStart = startOfWeek(startOfMonth(focusedDate), { weekStartsOn: WEEK_STARTS_ON });
			const gridEnd = endOfDay(addDays(gridStart, 41));
			return { start: gridStart, end: gridEnd };
		}
		case 'week': {
			return {
				start: startOfWeek(focusedDate, { weekStartsOn: WEEK_STARTS_ON }),
				end: endOfWeek(focusedDate, { weekStartsOn: WEEK_STARTS_ON }),
			};
		}
		case 'day':
		default: {
			return { start: startOfDay(focusedDate), end: endOfDay(focusedDate) };
		}
	}
}

/** The list of day columns a week/day time-grid should render. */
export function getDaysInRange(range: IDateRange): Date[] {
	return eachDayOfInterval({ start: startOfDay(range.start), end: startOfDay(range.end) });
}

/** Step the focused date forward/backward by one unit of the current view. */
export function shiftFocusedDate(viewMode: CalendarViewMode, focusedDate: Date, direction: 1 | -1): Date {
	switch (viewMode) {
		case 'month':
			return addMonths(focusedDate, direction);
		case 'week':
			return addWeeks(focusedDate, direction);
		case 'day':
		default:
			return addDays(focusedDate, direction);
	}
}

/** Human-readable heading for the toolbar, e.g. "June 2026" / "Jun 22 – 28, 2026". */
export function getRangeTitle(viewMode: CalendarViewMode, focusedDate: Date): string {
	switch (viewMode) {
		case 'month':
			return format(focusedDate, 'MMMM yyyy');
		case 'week': {
			const start = startOfWeek(focusedDate, { weekStartsOn: WEEK_STARTS_ON });
			const end = endOfWeek(focusedDate, { weekStartsOn: WEEK_STARTS_ON });
			const sameMonth = start.getMonth() === end.getMonth();
			const sameYear = start.getFullYear() === end.getFullYear();
			if (sameMonth) {
				return `${format(start, 'MMM d')} – ${format(end, 'd, yyyy')}`;
			}
			if (sameYear) {
				return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
			}
			return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
		}
		case 'day':
		default:
			return format(focusedDate, 'EEEE, MMM d, yyyy');
	}
}

/** Stable per-day key (local time) used to bucket meetings by day. */
export function getDayKey(date: Date): string {
	return format(date, 'yyyy-MM-dd');
}
